import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { NewScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import { ANY_SPORT_PLAYERS_SLUG_CARDS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary } from '@sorare/core/src/lib/glossary';

import { BuyLowestPrice } from '@sorare/marketplace/src/components/card/BuyLowestPrice';

import { BuyingPlayerDetailsFooter_anyPlayer } from './__generated__/index.graphql';

const Root = styled(Horizontal).attrs({ gap: 2 })``;

type Props = {
  anyPlayer: BuyingPlayerDetailsFooter_anyPlayer;
  rarity: Rarity;
  inSeason: boolean;
};

export const BuyingPlayerDetailsFooter = ({
  anyPlayer,
  rarity,
  inSeason,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { lowestPriceAnyCard } = anyPlayer;
  return (
    <Root>
      {lowestPriceAnyCard && (
        <BuyLowestPrice
          fullWidth
          size="medium"
          withLabel
          card={lowestPriceAnyCard}
        />
      )}
      <Button
        fullWidth
        color="secondary"
        size="medium"
        role="link"
        startIcon={<NewScarcityIcon rarity={rarity} size={2} />}
        to={`${generateSportContextPath(ANY_SPORT_PLAYERS_SLUG_CARDS, {
          params: {
            slug: anyPlayer.slug,
          },
        })}?${buildFilterQuery({
          [SEARCH_PARAMS.ON_SALE]: true,
          [SEARCH_PARAMS.RARITY]: rarity,
          [SEARCH_PARAMS.IN_SEASON]: inSeason,
        })}`}
      >
        <FormattedMessage {...glossary.listings} />
      </Button>
    </Root>
  );
};

BuyingPlayerDetailsFooter.fragments = {
  anyPlayer: gql`
    fragment BuyingPlayerDetailsFooter_anyPlayer on AnyPlayerInterface {
      slug
      lowestPriceAnyCard(inSeason: $inSeason, rarity: $rarity)
        @include(if: $withBuyingOptions) {
        slug
        ...BuyLowestPrice_anyCard
      }
    }
    ${BuyLowestPrice.fragments.anyCard}
  ` as TypedDocumentNode<BuyingPlayerDetailsFooter_anyPlayer>,
};
